import { ref, computed, onMounted } from "vue";
import { Position } from "geojson";
import { PlantingDTO } from "seedgreen-shared/dtos/PlantingDTO";
import { useDefaultPlantingsQuery } from "@/composables/useDefaultPlantingsQuery";

export function useNearestPlanting() {
	const { plantings } = useDefaultPlantingsQuery();

	const userPosition = ref<Position | null>(null);
	const isLocating = ref(false);
	const locationError = ref<string | null>(null);

	// Get the user's current location
	const getUserLocation = () => {
		if (!navigator.geolocation) {
			locationError.value =
				"Geolocation is not supported by your browser";
			return;
		}

		isLocating.value = true;
		locationError.value = null;

		navigator.geolocation.getCurrentPosition(
			(position) => {
				userPosition.value = [
					position.coords.longitude,
					position.coords.latitude,
				];
				isLocating.value = false;
			},
			(error) => {
				console.error("Error getting location:", error);
				locationError.value = `Error getting location: ${error.message}`;
				isLocating.value = false;
			},
			{
				enableHighAccuracy: true,
				timeout: 10000,
				maximumAge: 60000,
			},
		);
	};

	// Find the nearest planting
	const nearestPlanting = computed<PlantingDTO | undefined>(() => {
		if (
			!userPosition.value ||
			!plantings.value ||
			plantings.value.length === 0
		) {
			return undefined;
		}

		let closest: PlantingDTO | undefined;
		let closestDistance = Infinity;

		for (const planting of plantings.value) {
			const distance = planting.distanceTo(userPosition.value);

			if (distance !== undefined && distance < closestDistance) {
				closestDistance = distance;
				closest = planting;
			}
		}

		return closest;
	});

	// Distance to the nearest planting
	const distanceToNearest = computed(() => {
		if (!userPosition.value || !nearestPlanting.value) {
			return undefined;
		}

		return nearestPlanting.value.distanceTo(userPosition.value);
	});

	// Sort plantings by distance to user
	const plantingsByDistance = computed(() => {
		if (!userPosition.value || !plantings.value) {
			return plantings.value || [];
		}

		return [...plantings.value].sort((a, b) => {
			const distanceA = userPosition.value
				? (a.distanceTo(userPosition.value) ?? Infinity)
				: Infinity;
			const distanceB = userPosition.value
				? (b.distanceTo(userPosition.value) ?? Infinity)
				: Infinity;
			return distanceA - distanceB;
		});
	});

	// Get user location when component mounts
	onMounted(() => {
		getUserLocation();
	});

	return {
		userPosition,
		isLocating,
		locationError,
		plantings,
		nearestPlanting,
		distanceToNearest,
		plantingsByDistance,
		getUserLocation,
	};
}
